import { BaseNode, isFile, isFolder } from 'helpers/documents/types/base';
import {
  SelectionEntryDto,
  SelectionType,
} from 'services/api/api-client.types';

export type FileStatus = 'selected' | 'unselected';
export type FolderStatus = 'selected' | 'partial' | 'unselected';

export type FileNodeState = {
  id: string;
  path: string;
  type: 'file';
  status: FileStatus;
  parentId: string | undefined;
  documentId: number;
  name: string;
};

export type FolderNodeState = {
  id: string;
  path: string;
  type: 'folder';
  status: FolderStatus;
  parentId: string | undefined;
  childIds: string[];
  name: string;
};

export type NodeState = FileNodeState | FolderNodeState;

export const isFolderState = (node: NodeState): node is FolderNodeState =>
  node.type === 'folder';

export const isFileState = (node: NodeState): node is FileNodeState =>
  node.type === 'file';

export class SelectionStore {
  private nodes = new Map<string, NodeState>();
  private listeners = new Map<string, Set<() => void>>();
  private _rootIds: string[] = [];

  constructor(items: BaseNode[]) {
    this._rootIds = this.buildTree(items, undefined, '');
  }

  private buildTree(
    items: BaseNode[],
    parentId: string | undefined,
    parentPath: string,
  ): string[] {
    return items.map((item) => {
      const path = parentPath ? `${parentPath}/${item.name}` : item.name;

      if (isFolder(item)) {
        const childIds = this.buildTree(item.children, item.id, path);
        this.nodes.set(item.id, {
          id: item.id,
          path,
          type: 'folder',
          status: 'unselected',
          parentId,
          childIds,
          name: item.name,
        });
      } else if (isFile(item)) {
        this.nodes.set(item.id, {
          id: item.id,
          path,
          type: 'file',
          status: 'unselected',
          parentId,
          documentId: item.documentId,
          name: item.name,
        });
      } else {
        throw new Error(`Unknown node type for item: ${JSON.stringify(item)}`);
      }

      return item.id;
    });
  }

  subscribe(id: string, listener: () => void): () => void {
    if (!this.listeners.has(id)) this.listeners.set(id, new Set());
    this.listeners.get(id)!.add(listener);
    return () => this.listeners.get(id)?.delete(listener);
  }

  private notify(id: string) {
    this.listeners.get(id)?.forEach((l) => l());
  }

  getNode(id: string): NodeState | undefined {
    return this.nodes.get(id);
  }

  getRootIds(): string[] {
    return this._rootIds;
  }

  getSelectedNodes(): NodeState[] {
    return [...this.nodes.values()].filter((n) => n.status === 'selected');
  }

  getSelectedFiles(): FileNodeState[] {
    return this.getSelectedNodes().filter(isFileState);
  }

  getSelectionEntries(): SelectionEntryDto[] {
    return [...this.nodes.values()]
      .filter((n) => n.status === 'selected')
      .map((n) => {
        if (isFileState(n)) {
          return {
            documentId: n.documentId,
            initialDocumentId: parseInt(n.id),
            fullPath: n.path,
            selectionType: SelectionType.File,
          } as SelectionEntryDto;
        } else if (isFolderState(n)) {
          return {
            documentId: null,
            fullPath: n.path,
            selectionType: SelectionType.Folder,
          } as SelectionEntryDto;
        } else {
          throw new Error('Unknown node type');
        }
      });
  }

  private setStatus(id: string, status: FileStatus | FolderStatus) {
    const node = this.nodes.get(id);
    if (!node || node.status === status) return;
    this.nodes.set(id, { ...node, status } as NodeState);
    this.notify(id);
  }

  private recalculateFolder(folderId: string) {
    const folder = this.nodes.get(folderId);
    if (!folder || !isFolderState(folder)) return;

    if (folder.childIds.length === 0) {
      this.setStatus(folderId, 'unselected');
      if (folder.parentId) this.recalculateFolder(folder.parentId);
      return;
    }

    const statuses = folder.childIds.map((id) => this.nodes.get(id)?.status);
    const allSelected = statuses.every((s) => s === 'selected');
    const allUnselected = statuses.every((s) => s === 'unselected');

    const newStatus: FolderStatus = allSelected
      ? 'selected'
      : allUnselected
        ? 'unselected'
        : 'partial';

    this.setStatus(folderId, newStatus);

    if (folder.parentId) this.recalculateFolder(folder.parentId);
  }

  private applyToSubtree(id: string, selected: boolean) {
    const node = this.nodes.get(id);
    if (!node) return;

    const status = selected ? 'selected' : 'unselected';

    if (isFileState(node)) {
      this.setStatus(id, status);
    } else if (isFolderState(node)) {
      node.childIds.forEach((childId) =>
        this.applyToSubtree(childId, selected),
      );
      this.setStatus(id, status);
    }
  }

  toggle(id: string) {
    const node = this.nodes.get(id);
    if (!node) return;

    if (isFileState(node)) {
      this.setStatus(
        id,
        node.status === 'selected' ? 'unselected' : 'selected',
      );
      if (node.parentId) this.recalculateFolder(node.parentId);
    } else if (isFolderState(node)) {
      const shouldSelect = node.status !== 'selected';
      this.applyToSubtree(id, shouldSelect);
      if (node.parentId) this.recalculateFolder(node.parentId);
    }
  }

  toggleAll() {
    const allSelected = this._rootIds.every(
      (id) => this.nodes.get(id)?.status === 'selected',
    );
    this._rootIds.forEach((id) => this.applyToSubtree(id, !allSelected));
  }

  getRootStatus(): FolderStatus {
    if (this._rootIds.length === 0) return 'unselected';

    const statuses = this._rootIds.map((id) => this.nodes.get(id)?.status);
    if (statuses.every((s) => s === 'selected')) return 'selected';
    if (statuses.every((s) => s === 'unselected')) return 'unselected';
    return 'partial';
  }
}
